/**
 * Decision makers (TRUS-770): every named leader Lead Builder has found across
 * imported organizations, with where each one came from — IRS Form 990
 * officers, NPPES authorized officials, the organization's own website, or a
 * paid tool — and whether there is an address we can send to.
 *
 * "Find decision-makers" runs the registry-first search over every imported
 * provider organization not searched yet, as a job in the Jobs tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, Users } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { leadBuilderApi, type DecisionMakersPage } from '../../../shared/services/platformApi';
import { ContactProvenanceBadges, ContactReviewActions } from './ContactProvenance';

type SourceKey = '' | 'irs_990' | 'nppes' | 'website' | 'paid';
type Scope = 'registries' | 'registries_and_websites' | 'all';

export function DecisionMakersTab({ onOpenJobs }: { onOpenJobs: () => void }) {
  const { t } = useAppTranslation();
  const [data, setData] = useState<DecisionMakersPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<SourceKey>('');
  const [emailStatus, setEmailStatus] = useState('');
  const [review, setReview] = useState('');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<Scope>('registries_and_websites');
  const [starting, setStarting] = useState(false);
  const [jobMessage, setJobMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await leadBuilderApi.getDecisionMakers({
      source: source || undefined,
      email_status: emailStatus || undefined,
      review_status: review || undefined,
      q: search || undefined,
      page,
      page_size: 50,
    });
    setLoading(false);
    if (res.success && res.data) setData(res.data);
    else setError(res.error?.message || t('platform.leadBuilder.dm.loadError', 'Could not load decision makers.'));
  }, [source, emailStatus, review, search, page, t]);

  useEffect(() => {
    load();
  }, [load]);

  const start = async () => {
    setStarting(true);
    setJobMessage(null);
    const res = await leadBuilderApi.discoverDecisionMakers(scope);
    setStarting(false);
    setJobMessage(
      res.success && res.data
        ? res.data.message
        : res.error?.message || t('platform.leadBuilder.dm.startError', 'Could not start the search.'),
    );
  };

  const sources: { key: SourceKey; label: string; count?: number }[] = [
    { key: '', label: t('platform.leadBuilder.dm.source.all', 'All sources') },
    { key: 'irs_990', label: t('platform.leadBuilder.dm.source.irs990', 'IRS Form 990'), count: data?.by_source.irs_990 },
    { key: 'nppes', label: t('platform.leadBuilder.dm.source.nppes', 'NPPES'), count: data?.by_source.nppes },
    { key: 'website', label: t('platform.leadBuilder.dm.source.website', 'Their website'), count: data?.by_source.website },
    { key: 'paid', label: t('platform.leadBuilder.dm.source.paid', 'Paid tools'), count: data?.by_source.paid },
  ];
  const cov = data?.coverage;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Users className="w-4 h-4" aria-hidden="true" />
              {t('platform.leadBuilder.dm.title', 'Decision makers')}
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-3xl">
              {t(
                'platform.leadBuilder.dm.help',
                'Leaders of your imported organizations, found in public records (IRS Form 990, NPPES) and on their own websites before any paid tool.',
              )}
            </p>
            {cov && (
              <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
                <div>
                  <dt className="inline">{t('platform.leadBuilder.dm.cov.orgs', 'Imported organizations')}: </dt>
                  <dd className="inline font-semibold tabular-nums">{cov.provider_orgs.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="inline">{t('platform.leadBuilder.dm.cov.searched', 'Searched')}: </dt>
                  <dd className="inline font-semibold tabular-nums">{cov.searched.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="inline">{t('platform.leadBuilder.dm.cov.leader', 'With a named leader')}: </dt>
                  <dd className="inline font-semibold tabular-nums">{cov.with_leader.toLocaleString()}</dd>
                </div>
                <div>
                  <dt className="inline">{t('platform.leadBuilder.dm.cov.sendable', 'With an email we can send to')}: </dt>
                  <dd className="inline font-semibold tabular-nums">{cov.with_sendable.toLocaleString()}</dd>
                </div>
              </dl>
            )}
          </div>
          <div className="flex flex-col items-stretch gap-2 w-full sm:w-auto sm:min-w-[260px]">
            <label htmlFor="dm-scope" className="text-xs font-medium text-gray-700">
              {t('platform.leadBuilder.dm.scope', 'Search in')}
            </label>
            <select
              id="dm-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as Scope)}
              className="border rounded-md px-2 py-1.5 text-sm"
            >
              <option value="registries">{t('platform.leadBuilder.dm.scope.registries', 'Public records only (free)')}</option>
              <option value="registries_and_websites">
                {t('platform.leadBuilder.dm.scope.websites', 'Public records + their websites')}
              </option>
              <option value="all">{t('platform.leadBuilder.dm.scope.all', 'Everything, including paid tools')}</option>
            </select>
            <button
              type="button"
              onClick={start}
              disabled={starting}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {starting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {t('platform.leadBuilder.dm.start', 'Find decision-makers')}
            </button>
            {jobMessage && (
              <p className="text-xs text-gray-600">
                {jobMessage}{' '}
                <button type="button" onClick={onOpenJobs} className="text-blue-600 underline">
                  {t('platform.leadBuilder.dm.openJobs', 'Open Jobs')}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {sources.map((s) => (
          <button
            key={s.key || 'all'}
            type="button"
            onClick={() => {
              setSource(s.key);
              setPage(1);
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              source === s.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {s.label}
            {s.count !== undefined && <span className="ml-1 tabular-nums opacity-80">{s.count.toLocaleString()}</span>}
          </button>
        ))}
        <select
          id="dm-email-status"
          aria-label={t('platform.leadBuilder.dm.emailFilter', 'Email')}
          value={emailStatus}
          onChange={(e) => {
            setEmailStatus(e.target.value);
            setPage(1);
          }}
          className="border rounded-md px-2 py-1 text-xs"
        >
          <option value="">{t('platform.leadBuilder.dm.email.any', 'Any email')}</option>
          <option value="sendable">{t('platform.leadBuilder.dm.email.sendable', 'Can send')}</option>
          <option value="guess">{t('platform.leadBuilder.dm.email.guess', 'Guessed address')}</option>
          <option value="none">{t('platform.leadBuilder.dm.email.none', 'No email yet')}</option>
          <option value="invalid">{t('platform.leadBuilder.dm.email.invalid', 'Invalid')}</option>
        </select>
        <select
          id="dm-review"
          aria-label={t('platform.leadBuilder.dm.reviewFilter', 'Review')}
          value={review}
          onChange={(e) => {
            setReview(e.target.value);
            setPage(1);
          }}
          className="border rounded-md px-2 py-1 text-xs"
        >
          <option value="">{t('platform.leadBuilder.dm.review.any', 'Any review state')}</option>
          <option value="needs_review">{t('platform.leadBuilder.dm.review.needs', 'Needs review')}</option>
          <option value="approved">{t('platform.leadBuilder.dm.review.approved', 'Approved')}</option>
          <option value="rejected">{t('platform.leadBuilder.dm.review.rejected', 'Rejected')}</option>
        </select>
        <form
          className="flex items-center gap-1 ml-auto"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(query.trim());
            setPage(1);
          }}
        >
          <input
            id="dm-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('platform.leadBuilder.dm.search', 'Name, title or organization')}
            className="border rounded-md px-2 py-1 text-xs w-56"
          />
          <button type="submit" className="p-1.5 border rounded-md hover:bg-gray-50" aria-label={t('platform.leadBuilder.dm.searchBtn', 'Search')}>
            <Search className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </form>
      </div>

      {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md">{error}</div>}

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.leadBuilder.dm.col.person', 'Person')}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.leadBuilder.dm.col.org', 'Organization')}</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('platform.leadBuilder.dm.col.contact', 'Contact')}</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('platform.leadBuilder.dm.col.review', 'Review')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" aria-hidden="true" />
                  {t('platform.leadBuilder.dm.loading', 'Loading…')}
                </td>
              </tr>
            ) : data && data.items.length > 0 ? (
              data.items.map((p) => (
                <tr key={p.id} className="align-top">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">
                      {p.first_name} {p.last_name}
                    </p>
                    <p className="text-xs text-gray-500">{p.title || t('platform.leadBuilder.dm.noTitle', 'No title')}</p>
                    <ContactProvenanceBadges contact={p} />
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-900">{p.company_name || '—'}</p>
                    <p className="text-xs text-gray-500">
                      {[p.organization_type, [p.headquarters_city, p.headquarters_state].filter(Boolean).join(', ')]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    <p>{p.email || <span className="text-gray-400">{t('platform.leadBuilder.dm.noEmail', 'No email yet')}</span>}</p>
                    {p.phone && <p className="text-gray-500">{p.phone}</p>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.review_status === 'needs_review' ? (
                      <ContactReviewActions
                        contactId={p.id}
                        onDone={(status) =>
                          setData((prev) =>
                            prev ? { ...prev, items: prev.items.map((i) => (i.id === p.id ? { ...i, review_status: status } : i)) } : prev,
                          )
                        }
                      />
                    ) : (
                      <span className="text-xs text-gray-400">{p.review_status || ''}</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  {cov && cov.searched === 0
                    ? t(
                        'platform.leadBuilder.dm.emptyNotRun',
                        'No organizations have been searched yet. Choose where to search and click "Find decision-makers".',
                      )
                    : t('platform.leadBuilder.dm.empty', 'No one matches these filters.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span className="tabular-nums">
            {t('platform.leadBuilder.dm.pageOf', 'Page {{page}} of {{pages}} · {{total}} people', {
              page,
              pages,
              total: data.total.toLocaleString(),
            })}
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((n) => n - 1)} className="px-2 py-1 border rounded disabled:opacity-40">
              {t('platform.leadBuilder.dm.prev', 'Previous')}
            </button>
            <button type="button" disabled={page >= pages} onClick={() => setPage((n) => n + 1)} className="px-2 py-1 border rounded disabled:opacity-40">
              {t('platform.leadBuilder.dm.next', 'Next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
